import { useTranslation } from "react-i18next";
import PrimaryButton from "../../components/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../components/SecondaryButton/SecondaryButton";
import PageWrapper from "../PageWrapper/PageWrapper";
import Card from "../../components/Card/Card";
import { PermissionFlags } from "../../components/PermissionFlags/PermissionFlags";
import SlideOver from "../../components/SlideOver/SlideOver";
import { useEffect, useMemo, useState } from "react";
import BookingForm from "../../components/BookingForm/BookingForm";
import BookingTypeCard from "../../components/BookingTypeCard/BookingTypeCard";
import ListContainer from "../../components/ListContainer/ListContainer";
import ListElement from "../../components/ListElement/ListElement";
import ElementActions from "../../components/ElementActions/ElementActions";
import IconButton from "../../components/IconButton/IconButton";
import ConfirmationPopup from "../../components/ConfirmationPopup/ConfirmationPopup";
import { Icon } from "../../components/Icon/Icon";
import deleteIcon from "../../assets/images/bin.svg";
import edit from "../../assets/images/pen.svg";
import { useBookingPlans } from '../../hooks/useBookingPlans';
import { BookingPlan } from '../../api/BookingPlansAPI';
import NoDataCTA from "../../components/NoDataCTA/NoDataCTA";
import Switch from "../../components/Switch/Switch";
import { Settings } from "../../store/reducers/AccountSlice";
import { useSelector } from "react-redux";
import CopyText from '../../components/CopyText/CopyText';
import { ChevronDown, ChevronUp, Globe, Instagram, Menu, X, Monitor, Smartphone } from "lucide-react";
import { ButtonV2 } from "../../components/ButtonV2/ButtonV2";
import { Link } from "react-router-dom";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../components/Collapsible/Collapsible";
import { AdminRestricted } from "../../components/AdminRestricted/AdminRestricted";
import { MeetingType } from "../../shared/meetingTypeToTranslationKey";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/Dialog/Dialog";
import { InputV2 } from "../../components/InputV2/InputV2";
import { InstallInstructionsModal } from "./InstallInstructionsModal/InstallInstructionsModal";
import { TabV2, TabsList, TabsTrigger, TabsContent } from "../../components/TabV2/TabV2";
import FileUploadWrapper from "../../components/FileUploadWrapper/FileUploadWrapper";
import SectionTitle from "../../components/SectionTitle/SectionTitle";
import ConfigAPI, { Config } from "../../api/ConfigAPI";
import CONFIG from "../../config/config";
import { useImageUploader } from "../../hooks/useImageUploader";
import { toastService } from "../../services/ToastService";
import BookingPlansAPI, { BookingBehaviourConfigPayload, BookingBackgroundConfigPayload } from "../../api/BookingPlansAPI";
import type {
  BookingBackgroundModeMobileConfig,
} from "../../types/booking-background";
import { cn } from "../../lib/utils";
import { useAliasValidation } from '../../hooks/useAliasValidation';

const EMPTY_BOOKING: BookingPlan = {
  name: "",
  internalType: 'VISIT' as MeetingType,
  customerInfo: "",
  automaticallyAccept: false,
  requiresFee: false,
  duration: "60",
  useCalendarAvailability: true,
  enabled: true,
  weekdayHours: {
    Monday: { enabled: true, opening: "09:00", closing: "17:00" },
    Tuesday: { enabled: true, opening: "09:00", closing: "17:00" },
    Wednesday: { enabled: true, opening: "09:00", closing: "17:00" },
    Thursday: { enabled: true, opening: "09:00", closing: "17:00" },
    Friday: { enabled: true, opening: "09:00", closing: "17:00" },
    Saturday: { enabled: true, opening: "10:00", closing: "16:00" },
    Sunday: { enabled: false, opening: "", closing: "" },
  },
  customerData: {
    requestEmail: true,
    requestPhone: true,
    requestName: true,
    requestBudget: false,
    requestSize: false,
    requestWeddingDate: false,
    requestNumberOfPeople: false,
    allowSpecialComments: false,
    requestWhereHeardFrom: false,
  },
  budgetRanges: [],
  brideCanCancel: false,
  requiresRefundableFee: false,
  useAdditionalRestrictions: false,
  useOnlinePayments: false,
};

const BookingSettings = () => {
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslation();

  return (
    <div className="w-full mt-4 p-4 bg-white rounded-lg shadow-sm">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <ButtonV2 variant="ghost" className="w-full justify-between">
            {t("bookings.settings.cta")}
            {isOpen ? (
              <ChevronUp className="h-4 w-4 ml-2" />
            ) : (
              <ChevronDown className="h-4 w-4 ml-2" />
            )}
          </ButtonV2>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2 space-y-2">
          <p className="text-sm text-gray-600">
            {t("bookings.settings.description")}
          </p>
          <ButtonV2 asChild size="sm">
            <Link to="/settings">{t("bookings.settings.button")}</Link>
          </ButtonV2>
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}

const BookingLinkSection = ({ bookingLink, customLinkText, onCustomLinkTextChange }: { 
  bookingLink: string, 
  customLinkText: string;
  onCustomLinkTextChange: (text: string) => void; }) => {

  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [tempCustomLinkText, setTempCustomLinkText] = useState(customLinkText);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  
  const { validateAliasFormat, checkAliasAvailability, debouncedCheckAvailability, isChecking, isValidationInProgress, currentAbortController } = useAliasValidation();

  const validateCustomLinkText = (text: string): boolean => {
    const validation = validateAliasFormat(text);
    setError(validation.error);
    setAvailabilityError(null);
    return validation.isValid;
  };

  const handleCustomLinkTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value.toLowerCase();
    setTempCustomLinkText(newText);
    setHasChanges(newText !== customLinkText);
    
    if (newText === customLinkText) {
      setError(null);
      setAvailabilityError(null);
      setHasChanges(false);
    } else {
      const isValid = validateCustomLinkText(newText);
      
      if (isValid && newText.length >= 3) {
        debouncedCheckAvailability(
          newText,
          (alias: string) => import('../../api/BookingPlansAPI').then(api => api.default.checkAliasAvailability(alias, currentAbortController?.signal)),
          (result) => {
            if (!result.isAvailable) {
              setAvailabilityError(result.error || 'Alias is not available');
            } else {
              setAvailabilityError(null);
            }
          }
        );
      }
    }
  };

  const handleSaveCustomLink = async () => {
    if (!validateCustomLinkText(tempCustomLinkText)) return;
    
    const availabilityCheck = await checkAliasAvailability(
      tempCustomLinkText, 
      (alias: string) => import('../../api/BookingPlansAPI').then(api => api.default.checkAliasAvailability(alias))
    );
    
    if (!availabilityCheck.isAvailable) {
      setAvailabilityError(availabilityCheck.error || 'Alias is not available');
      return;
    }
    
    onCustomLinkTextChange(tempCustomLinkText);
    setIsEditModalOpen(false);
    setHasChanges(false);
    setError(null);
    setAvailabilityError(null);
  };

  const handleCancel = () => {
    setTempCustomLinkText(customLinkText);
    setError(null);
    setAvailabilityError(null);
    setHasChanges(false);
    setIsEditModalOpen(false);
  };

  const renderBookingLink = () => {
    const parts = bookingLink.split('/');
    const customPart = parts.pop();
    const basePart = parts.join('/');
  
    return (
      <div className="flex flex-wrap items-center gap-2 bg-gray-100 p-2 rounded-md overflow-hidden">
        <span className="text-gray-500 truncate w-full sm:w-auto">
          {basePart}/
        </span>
        <span className="font-medium truncate w-full sm:w-auto">
          {customPart}
        </span>
        <div className="shrink-0">
          <CopyText text={bookingLink} />
        </div>
      </div>
    );
  };

  return (
    <div className="mt-6 bg-gray-50 rounded-lg p-4 w-full overflow-x-auto">
      <div className="flex flex-wrap items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">{t("bookings.widget.copy")}</h3>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-primary-600 hover:text-primary-700 focus:outline-none"
        >
          {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </button>
      </div>
      {renderBookingLink()}
      <div className="mt-4 sm:space-x-1">
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogTrigger asChild>
            <ButtonV2 variant="outline" size="sm" className="text-xs sm:text-sm">
              {t("bookings.widget.customize_link")}
            </ButtonV2>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("bookings.widget.customize_link_title")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="relative">
                <InputV2
                  value={tempCustomLinkText}
                  onChange={handleCustomLinkTextChange}
                  placeholder={t("bookings.widget.custom_link_placeholder")!}
                />
                {isValidationInProgress && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="validation-spinner"></div>
                  </div>
                )}
              </div>
              {error && <p className="text-red-500 text-sm">{error}</p>}
              {availabilityError && <p className="text-red-500 text-sm">{availabilityError}</p>}
              <div className="flex justify-center space-x-3">
                <SecondaryButton text={t("global.cancel")!} fn={handleCancel} />
                <PrimaryButton 
                  text={t("global.save")!} 
                  fn={handleSaveCustomLink} 
                  isDisabled={!!error || isChecking || isValidationInProgress || !hasChanges || !!availabilityError}
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>
        <InstallInstructionsModal />
      </div>
      {isExpanded && 
        <div className="mt-4 space-y-3">
          <p className="text-sm text-gray-600">{t("bookings.widget.instructions")}</p>
          <ul className="space-y-2">
            <li className="flex items-center space-x-2 text-sm">
              <Globe className="h-4 w-4 text-primary-600" />
              <span>{t("bookings.widget.website")}</span>
            </li>
            <li className="flex items-center space-x-2 text-sm">
              <Instagram className="h-4 w-4 text-primary-600" />
              <span>{t("bookings.widget.instagram")}</span>
            </li>
            <li className="flex items-center space-x-2 text-sm">
              <Menu className="h-4 w-4 text-primary-600" />
              <span>{t("bookings.widget.menu")}</span>
            </li>
          </ul>
        </div>
      }
    </div>
  );
};

const BookingsPage = () => {
  const { t } = useTranslation();
  const [editOpen, setEditOpen] = useState<boolean>(false);
  const [formData, setFormData] = useState<BookingPlan>(EMPTY_BOOKING);
  const [isOperationLoading, setIsOperationLoading] = useState<boolean>(false);
  const [customLinkText, setCustomLinkText] = useState<string>("");
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [reorderedBookingPlans, setReorderedBookingPlans] = useState<BookingPlan[]>([]);
  const settings: Settings = useSelector((state: any) => state.settings.value);
  const [config, setConfig] = useState<Config | null>(null);
  const [isConfigLoading, setIsConfigLoading] = useState(false);
  const [backgroundScreen, setBackgroundScreen] = useState<'desktop' | 'mobile'>('desktop');
  const { uploadImage } = useImageUploader(undefined, (response) => response.data.url, 'images');

  const {
    bookingPlans,
    isLoading,
    createBookingPlan,
    updateBookingPlan,
    deleteBookingPlan,
    setBookingLink,
    reorderBookingPlans
  } = useBookingPlans();

  const handleEdit = (booking: BookingPlan) => {
    setFormData(booking);
    setEditOpen(true);
  };

  const handleDelete = async (booking: BookingPlan) => {
    setIsOperationLoading(true);
    await deleteBookingPlan(booking.id!);
    setIsOperationLoading(false);
  };

  const handleQuickSave = async (booking: BookingPlan) => {
    await updateBookingPlan(booking.id!, { ...booking, enabled: !booking.enabled })
  };

  const bookingLink = `${window.location.origin}/bookings/${customLinkText || settings.bookingWidgetId}`;

  useEffect(() => {
    setCustomLinkText(settings.bookingWidgetId || "");
  }, [settings.bookingWidgetId]);

  useEffect(() => {
    setReorderedBookingPlans(bookingPlans);
  }, [bookingPlans]);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        setIsConfigLoading(true);
        const res = await ConfigAPI.get();
        setConfig(res.data);
      } catch (err) {
        console.error("Failed to load configuration for booking background:", err);
      } finally {
        setIsConfigLoading(false);
      }
    };
    loadConfig();
  }, []);

  const handleCustomLinkTextChange = (text: string) => {
    setCustomLinkText(text);
    setBookingLink(text);
  };

  const handleBackgroundImageUpload = async (file: File) => {
    if (!file || !config) return;
    try {
      setIsConfigLoading(true);
      const fileUrl = await uploadImage(file);
      if (!fileUrl) {
        throw new Error("File upload failed");
      }
      const payload: BookingBackgroundConfigPayload = {};

      if (backgroundScreen === "desktop") {
        payload.bookingBackgroundImageUrl = fileUrl;
        // If no explicit desktop/background mode has been configured yet,
        // default desktop (and legacy/global) to BACKGROUND on first image upload.
        if (!config.bookingBackgroundModeDesktop && !config.bookingBackgroundMode) {
          payload.bookingBackgroundModeDesktop = "BACKGROUND";
          payload.bookingBackgroundMode = "BACKGROUND";
        }
      } else {
        payload.bookingBackgroundImageUrlMobile = fileUrl;
        if (!config.bookingBackgroundModeMobile) {
          payload.bookingBackgroundModeMobile = "BACKGROUND";
        }
      }

      const response = await BookingPlansAPI.updateBookingBackground(payload);
      setConfig(response.data);
    } catch (error) {
      console.error("Failed to upload booking background image:", error);
      toastService.showToast(t("toast.errors.logo") ?? "Failed to upload image", "error");
    } finally {
      setIsConfigLoading(false);
    }
  };

  const handleRemoveBackgroundImage = async () => {
    if (!config) return;
    try {
      setIsConfigLoading(true);
      const response = await BookingPlansAPI.deleteBookingBackground();
      setConfig(response.data);
    } catch (error) {
      console.error("Failed to remove booking background image:", error);
      toastService.showToast(t("toast.errors.logo") ?? "Failed to remove image", "error");
    } finally {
      setIsConfigLoading(false);
    }
  };

  const handleBackgroundModeChange = async (
    mode: BookingBackgroundModeMobileConfig,
    target: 'desktop' | 'mobile'
  ) => {
    if (!config) return;
    try {
      setIsConfigLoading(true);
      const payload: BookingBehaviourConfigPayload = {};

      if (target === 'desktop') {
        if (mode === 'BACKGROUND' || mode === 'HEADER') {
          // Keep legacy/global mode in sync with the desktop setting for older widgets
          (payload as any).bookingBackgroundMode = mode;
          (payload as any).bookingBackgroundModeDesktop = mode;
        }
      }

      if (target === 'mobile') {
        if (mode === '') {
          // Clearing mobile-specific mode: send empty string, backend treats blank as \"no override\"
          (payload as any).bookingBackgroundModeMobile = '';
        } else if (mode === 'BACKGROUND' || mode === 'HEADER') {
          (payload as any).bookingBackgroundModeMobile = mode;
        }
      }

      const response = await BookingPlansAPI.updateBookingBackground(payload as any);
      setConfig((prev) =>
        prev
          ? {
              ...prev,
              ...response.data,
              bookingBackgroundImageUrl:
                response.data.bookingBackgroundImageUrl ?? prev.bookingBackgroundImageUrl,
              bookingBackgroundImageUrlMobile:
                response.data.bookingBackgroundImageUrlMobile ?? prev.bookingBackgroundImageUrlMobile,
            }
          : response.data
      );
    } catch (error) {
      console.error("Failed to update booking background mode:", error);
      toastService.showToast(t("toast.errors.logo") ?? "Failed to update background mode", "error");
    } finally {
      setIsConfigLoading(false);
    }
  };

  const handleBehaviourChange = async (payload: BookingBehaviourConfigPayload) => {
    if (!config) return;
    try {
      setIsConfigLoading(true);
      const response = await BookingPlansAPI.updateBookingBehaviour(payload);
      setConfig((prev) => ({
        ...(prev || {}),
        ...response.data,
      }));
    } catch (error) {
      console.error("Failed to update booking behaviour configuration:", error);
      toastService.showToast(t("toast.errors.logo") ?? "Failed to update booking behaviour", "error");
    } finally {
      setIsConfigLoading(false);
    }
  };

  const handleAutoSelectSingleBookingTypeToggle = () => {
    const current = config?.autoSelectSingleBookingType ?? false;
    const next = !current;
    void handleBehaviourChange({ autoSelectSingleBookingType: next });
  };

  const handleHideBrandOnBookingPageToggle = () => {
    const currentHide = config?.hideBrandOnBookingPage ?? false;
    const nextHide = !currentHide;
    void handleBehaviourChange({ hideBrandOnBookingPage: nextHide });
  };

  const handleSave = async () => {
    setIsOperationLoading(true);
    if (formData.id) {
      await updateBookingPlan(formData.id, formData);
    } else {
      await createBookingPlan(formData);
    }
    setIsOperationLoading(false);
    setEditOpen(false);
    setFormData(EMPTY_BOOKING);
    setFormErrors({});
  };

  const handleNewBooking = () => {
    setFormData(EMPTY_BOOKING);
    setEditOpen(true);
  }

  const handleBookingTypesReorder = async (reorderedItems: BookingPlan[]) => {
    setReorderedBookingPlans(reorderedItems);
    const orderedIds = reorderedItems.map(item => item.id!).filter(id => id !== undefined);
    await reorderBookingPlans(orderedIds);
  };

  const desktopBgUrl = config?.bookingBackgroundImageUrl;
  const mobileBgUrl = config?.bookingBackgroundImageUrlMobile;
  const backgroundPreviewSrc = useMemo(() => {
    if (backgroundScreen === "desktop") {
      return desktopBgUrl;
    }
    return mobileBgUrl || desktopBgUrl;
  }, [backgroundScreen, desktopBgUrl, mobileBgUrl]);

  return (
    <PageWrapper>
      <>
        <div className="space-y-5">
          <Card>
            <div className="flex flex-wrap">
              <div className="container flex flex-wrap space-x-5">
                <PermissionFlags requiredFlags={['edit-customers']}>
                  <PrimaryButton text={t("bookings.empty.cta")!} fn={handleNewBooking} />
                </PermissionFlags>
              </div>
              <BookingLinkSection 
                bookingLink={bookingLink} 
                customLinkText={customLinkText}
                onCustomLinkTextChange={handleCustomLinkTextChange}
              />
            </div>
          </Card>
          <Card>
            <>
              <TabV2 defaultValue="types" className="w-full">
                <TabsList className="grid w-full grid-cols-2 py-[15px]">
                  <TabsTrigger value="types">
                    {t("bookings.tab.types") || "Appointment types"}
                  </TabsTrigger>
                  <TabsTrigger value="advanced">
                    {t("bookings.tab.advanced") || "Advanced configuration"}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="types">
                  <div className="overflow-x-scroll w-full">
                    <ListContainer
                      isLoading={isLoading}
                      elements={reorderedBookingPlans}
                      enableDragAndDrop
                      onMovedItem={handleBookingTypesReorder}
                      emptyListComponent={() => 
                        <NoDataCTA
                          headline={t("bookings.empty.title")!}
                          subtext={t("bookings.empty.body")!}
                          primaryActionText={t("bookings.empty.cta")}
                          onPrimaryAction={handleNewBooking}
                        /> }
                      toElementFn={(booking: BookingPlan) => (
                        <ListElement
                          key={booking.id}
                          css="bg-white text-black hover:bg-darker-gold-100"
                        >
                          <ElementActions
                            actions={
                              <div className="flex space-x-3 items-center">
                                <Switch
                                  id={`${booking.name}-${booking.enabled}`}
                                  checked={booking.enabled}
                                  onChange={() => handleQuickSave(booking)}
                                />
                                <IconButton onClick={() => handleEdit(booking)}>
                                  <Icon url={edit} cssStyles="w-5"/>
                                </IconButton>
                                <PermissionFlags requiredFlags={['remove-meetings']}>
                                  <ConfirmationPopup onConfirm={() => handleDelete(booking)} confirmText={t("global.confirm")} title={`${t("global.remove")}: ${booking.name}`}>
                                    <Icon url={deleteIcon} cssStyles="w-5" />
                                  </ConfirmationPopup>
                                </PermissionFlags>
                              </div>
                            }
                          >
                            <BookingTypeCard booking={booking} onEdit={() => handleEdit(booking)}/>
                          </ElementActions>
                        </ListElement>
                      )}
                    />
                  </div>
                  <AdminRestricted >
                    <BookingSettings />
                  </AdminRestricted>
                </TabsContent>

                <TabsContent value="advanced">
                  <div className="py-4 space-y-8">
                    <p className="text-sm text-gray-600">
                      {t("bookings.advanced.description") || "Configure advanced options for your online booking widget, including the background image."}
                    </p>

                    {/* Behaviour section */}
                    <div className="space-y-2 border border-gray-100 rounded-lg p-4">
                      <SectionTitle
                        text={t("bookings.advanced.behaviour.title")}
                        clazz="text-base"
                      />
                      <p className="text-xs text-gray-600">
                        {t("bookings.advanced.behaviour.subtitle")}
                      </p>

                      <div className="flex flex-col gap-4 mt-2">
                        <div className="flex items-center justify-between">
                          <div className="space-y-1 pr-4">
                            <p className="text-sm font-medium text-gray-900">
                              {t("bookings.advanced.behaviour.auto_select_single")}
                            </p>
                            <p className="text-xs text-gray-600">
                              {t("bookings.advanced.behaviour.auto_select_single.description")}
                            </p>
                          </div>
                          <Switch
                            id="auto-select-single-booking-type"
                            checked={config?.autoSelectSingleBookingType ?? false}
                            disabled={isConfigLoading}
                            onChange={handleAutoSelectSingleBookingTypeToggle}
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="space-y-1 pr-4">
                            <p className="text-sm font-medium text-gray-900">
                              {t("bookings.advanced.behaviour.show_brand")}
                            </p>
                            <p className="text-xs text-gray-600">
                              {t("bookings.advanced.behaviour.show_brand.description")}
                            </p>
                          </div>
                          <Switch
                            id="hide-brand-on-booking-page"
                            checked={config?.hideBrandOnBookingPage ?? false}
                            disabled={isConfigLoading}
                            onChange={handleHideBrandOnBookingPageToggle}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Background section moved to bottom */}
                    <div className="space-y-2 border border-gray-100 rounded-lg p-4">
                      <SectionTitle
                        text={t("bookings.advanced.background.title") || "Background image"}
                        clazz="text-base"
                      />
                      <p className="text-xs text-gray-600">
                        {t("bookings.advanced.background.subtitle") ||
                          "Upload an image to use as the background of your public booking page."}
                      </p>

                      {(desktopBgUrl || mobileBgUrl) && (
                        <div className="mt-4">
                          <p className="text-sm text-gray-600 mb-2">
                            {t("bookings.advanced.background.preview") || "Current background image:"}
                          </p>
                          {backgroundScreen === "mobile" && !mobileBgUrl && desktopBgUrl && (
                            <p className="text-xs text-gray-500 mb-2">
                              {t("bookings.advanced.background.preview.mobile_fallback")}
                            </p>
                          )}
                          {backgroundScreen === "desktop" && !desktopBgUrl && mobileBgUrl && (
                            <p className="text-xs text-gray-500 mb-2">
                              {t("bookings.advanced.background.preview.desktop_missing")}
                            </p>
                          )}
                          {backgroundPreviewSrc && (
                            <div className="relative max-w-xl rounded-lg overflow-hidden border bg-gray-50">
                              <img
                                src={backgroundPreviewSrc}
                                alt="Booking background preview"
                                className="w-full h-48 object-cover"
                              />
                              <button
                                type="button"
                                onClick={handleRemoveBackgroundImage}
                                className="absolute top-2 right-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/80 text-gray-700 hover:bg-white shadow-sm"
                                aria-label={t("bookings.advanced.background.remove") || "Remove background image"}
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {(desktopBgUrl || mobileBgUrl) && (
                        <div className="mt-4 space-y-4">
                          <div className="flex flex-col gap-2">
                            <p className="text-sm font-medium text-gray-800">
                              {t("bookings.advanced.background.mode.title") || "Display mode"}
                            </p>
                            <div className="inline-flex w-fit items-center gap-2 rounded-full bg-gray-100 p-1">
                              <button
                                type="button"
                                className={cn(
                                  "flex items-center gap-1 px-3 py-1 text-xs rounded-full transition-colors",
                                  backgroundScreen === 'desktop'
                                    ? "bg-white text-gray-900 shadow-sm"
                                    : "text-gray-600 hover:text-gray-900"
                                )}
                                disabled={isConfigLoading}
                                onClick={() => setBackgroundScreen('desktop')}
                              >
                                <Monitor className="h-3 w-3" />
                                <span>
                                  {t("bookings.advanced.background.screen.desktop") || "Desktop"}
                                </span>
                              </button>
                              <button
                                type="button"
                                className={cn(
                                  "flex items-center gap-1 px-3 py-1 text-xs rounded-full transition-colors",
                                  backgroundScreen === 'mobile'
                                    ? "bg-white text-gray-900 shadow-sm"
                                    : "text-gray-600 hover:text-gray-900"
                                )}
                                disabled={isConfigLoading}
                                onClick={() => setBackgroundScreen('mobile')}
                              >
                                <Smartphone className="h-3 w-3" />
                                <span>
                                  {t("bookings.advanced.background.screen.mobile") || "Mobile"}
                                </span>
                              </button>
                            </div>
                          </div>

                          <p className="text-xs text-gray-600">
                            {backgroundScreen === 'desktop'
                              ? t("bookings.advanced.background.editing.desktop") ||
                                "You are editing the desktop version of the booking page."
                              : t("bookings.advanced.background.editing.mobile") ||
                                "You are editing the mobile version of the booking page."}
                          </p>

                          <div className="space-y-3">
                            {backgroundScreen === 'desktop' ? (
                              <div className="inline-flex items-center gap-2 rounded-full bg-gray-100 p-1">
                                <button
                                  type="button"
                                  className={cn(
                                    "px-3 py-1 text-xs rounded-full transition-colors",
                                    (config.bookingBackgroundModeDesktop ||
                                      config.bookingBackgroundMode ||
                                      'BACKGROUND') === 'BACKGROUND'
                                      ? "bg-white text-gray-900 shadow-sm"
                                      : "text-gray-600 hover:text-gray-900"
                                  )}
                                  disabled={isConfigLoading}
                                  onClick={() => handleBackgroundModeChange('BACKGROUND', 'desktop')}
                                >
                                  {t("bookings.advanced.background.mode.background") ||
                                    "Use as full background"}
                                </button>
                                <button
                                  type="button"
                                  className={cn(
                                    "px-3 py-1 text-xs rounded-full transition-colors",
                                    (config.bookingBackgroundModeDesktop ||
                                      config.bookingBackgroundMode) === 'HEADER'
                                      ? "bg-white text-gray-900 shadow-sm"
                                      : "text-gray-600 hover:text-gray-900"
                                  )}
                                  disabled={isConfigLoading}
                                  onClick={() => handleBackgroundModeChange('HEADER', 'desktop')}
                                >
                                  {t("bookings.advanced.background.mode.header") || "Use as header image"}
                                </button>
                              </div>
                            ) : (
                              <div className="inline-flex items-center gap-2 rounded-full bg-gray-100 p-1">
                                <button
                                  type="button"
                                  className={cn(
                                    "px-3 py-1 text-xs rounded-full transition-colors",
                                    !config.bookingBackgroundModeMobile
                                      ? "bg-white text-gray-900 shadow-sm"
                                      : "text-gray-600 hover:text-gray-900"
                                  )}
                                  disabled={isConfigLoading}
                                  onClick={() => handleBackgroundModeChange('', 'mobile')}
                                >
                                  {t("bookings.advanced.background.mode.mobile.none") ||
                                    "No image on mobile"}
                                </button>
                                <button
                                  type="button"
                                  className={cn(
                                    "px-3 py-1 text-xs rounded-full transition-colors",
                                    config.bookingBackgroundModeMobile === 'BACKGROUND'
                                      ? "bg-white text-gray-900 shadow-sm"
                                      : "text-gray-600 hover:text-gray-900"
                                  )}
                                  disabled={isConfigLoading}
                                  onClick={() => handleBackgroundModeChange('BACKGROUND', 'mobile')}
                                >
                                  {t("bookings.advanced.background.mode.mobile.background") ||
                                    "Mobile background"}
                                </button>
                                <button
                                  type="button"
                                  className={cn(
                                    "px-3 py-1 text-xs rounded-full transition-colors",
                                    config.bookingBackgroundModeMobile === 'HEADER'
                                      ? "bg-white text-gray-900 shadow-sm"
                                      : "text-gray-600 hover:text-gray-900"
                                  )}
                                  disabled={isConfigLoading}
                                  onClick={() => handleBackgroundModeChange('HEADER', 'mobile')}
                                >
                                  {t("bookings.advanced.background.mode.mobile.header") ||
                                    "Mobile header"}
                                </button>
                              </div>
                            )}

                            <div className="space-y-1 text-xs text-gray-600">
                              <div className="flex items-center gap-2">
                                <Monitor className="h-3 w-3" />
                                <span>
                                  {t("bookings.advanced.background.screen.desktop") || "Desktop"}
                                  {": "}
                                  {(() => {
                                    const value =
                                      config.bookingBackgroundModeDesktop ||
                                      config.bookingBackgroundMode ||
                                      'BACKGROUND';
                                    if (value === 'HEADER') {
                                      return (
                                        t("bookings.advanced.background.mode.header") || "Header image"
                                      );
                                    }
                                    return (
                                      t("bookings.advanced.background.mode.background") || "Full background"
                                    );
                                  })()}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Smartphone className="h-3 w-3" />
                                <span>
                                  {t("bookings.advanced.background.screen.mobile") || "Mobile"}
                                  {": "}
                                  {(() => {
                                    const value = config.bookingBackgroundModeMobile;
                                    if (!value) {
                                      return (
                                        t("bookings.advanced.background.mode.mobile.none") || "No image"
                                      );
                                    }
                                    if (value === 'HEADER') {
                                      return (
                                        t("bookings.advanced.background.mode.mobile.header") || "Header image"
                                      );
                                    }
                                    return (
                                      t("bookings.advanced.background.mode.mobile.background") ||
                                      "Full background"
                                    );
                                  })()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <FileUploadWrapper
                        label={
                          backgroundScreen === "desktop"
                            ? "bookings.advanced.background.upload"
                            : "bookings.advanced.background.upload.mobile"
                        }
                        onChange={(file) => {
                          if (file) {
                            handleBackgroundImageUpload(file as File);
                          }
                        }}
                        allowedTypes={CONFIG.IMAGE_TYPES}
                        isDisabled={isConfigLoading}
                        maxSize={2.5}
                        allowedMultipleFiles={false}
                      />
                    </div>
                  </div>
                </TabsContent>
              </TabV2>
            </>
          </Card>
        </div>
        <SlideOver 
          isOpen={editOpen}
          setIsOpen={setEditOpen}
          title={formData.name || t("bookings.form.title")!}
          primaryButton={{
            label: t("global.save"),
            onClick: handleSave
          }}
          secondaryButton={{
            label: t("global.cancel"),
            onClick: () => setEditOpen(false)
          }}
          isLoading={isOperationLoading}
          hasErrors={Object.keys(formErrors).length > 0}
        >
          <BookingForm booking={formData} onChange={(updatedBooking, errors) => {setFormData(updatedBooking);setFormErrors(errors);}}  />
        </SlideOver>
      </>
    </PageWrapper>
  );
};

export default BookingsPage;